import { forwardRef, useCallback, useState } from 'react';
import { Modal, Button as RBButton } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Icon from './Icon';
import { useTranslation } from 'react-i18next';

export const toString = () => '<button>';

/**
 * General purpose Bootstrap Button component.
 * @component
 */
const Button = forwardRef(({children, confirmTitle, confirmMessage, processing, disabled, onClick, ...props}, ref) => {
    const { t } = useTranslation();
    const [pending, setPending] = useState(false);

    const handleClick = useCallback((e) => {
        if (confirmMessage || confirmTitle) {
            e.preventDefault();
            e.stopPropagation();
            setPending(true);
        } else {
            onClick(e);
        }
    }, [confirmMessage, confirmTitle, onClick]);

    const handleConfirmation = useCallback((e, result) => {
        e?.preventDefault();
        e?.stopPropagation();
        setPending(false);
        if (result) {
            onClick();
        }
    }, [onClick]);

    return <>
        { confirmMessage ? <Modal show={pending} onHide={() => handleConfirmation(null,false)}>
            { confirmTitle ? <Modal.Header closeButton>
                <Modal.Title>{confirmTitle}</Modal.Title>
            </Modal.Header> : null }
            { confirmMessage ? <Modal.Body>
                    {confirmMessage}
            </Modal.Body> : null }
            <Modal.Footer>
                <RBButton kind="ok" variant="primary" onClick={(e) => handleConfirmation(e,true)}><Icon intent="ok"></Icon> {t('ok')}</RBButton>
                <RBButton kind="cancel" variant="default" onClick={(e) => handleConfirmation(e,false)}><Icon intent="cancel"></Icon> {t('cancel')}</RBButton>
            </Modal.Footer>
        </Modal> : null }
        <RBButton ref={ref} onClick={handleClick} disabled={disabled || processing} {...props}>{!processing ? children : t('processing')}</RBButton>
    </>
})

Button.propTypes = {
    /**
     * Components that make up the button label
     */
    children: PropTypes.node.isRequired,

    /**
     * Confirmation message to display before carrying out the button's action
     */
    confirmMessage: PropTypes.string,

    /**
     * Title to display on the confirmation dialog shown before carrying out the button's action
     */
    confirmTitle: PropTypes.string,

    /**
     * Function to call when the button is clicked
     */
    onClick: PropTypes.func.isRequired
}

export default Button;